import authClient from './oktaAuth'
import ClientPortalAPIService from './../services/api/ClientPortal/ApiService'
import { cloneDeep } from 'lodash'
import { SETTINGS } from './../SETTINGS'
import Sentry from './../modules/SantryIO'

export class UserModule {
    hasFetchedUserFromServer = false
    hasFetchedUserPermissionsFromServer = false
    // Default values
    userInfo = {}
    userSettings = {
        airportCode: 'ICAO',
        mesaurementUnits: 'Metric',
        pinnedElements: [],
        airportsWatchlist: [],
        followedAirports: [],
        trackedAircrafts: [],
        routes: [],
        latestDismissedCriticalAlertID: '',
        latestDismissedAviationAlertID: [],
        filters: JSON.stringify({
            enableAlerts: true,
            enableAviationAlerts: true,
            alertSeverities: SETTINGS.INTERNATIONAL_SOS_SEVERITIES,
            timeFrame: SETTINGS.FILTERS_DEFAULT_TIMEFRAME,
            alertFilters: {},
            aviationFilters: {},
            maritimeFilters: {
                enabled: true
            }
        }),
        comstat: JSON.stringify({
            selectedAirports: [],
            allAirports: [],
            startDate: '',
            endDate: ''
        }),
        briefBuilder: null
    }

    userPermissions = [] // basic, medical

    constructor() { }

    async getUser() {
        const userInfo = await new Promise((resolve, reject) => {
            authClient.token.getUserInfo().then(user => {
                console.log('getUserInfo from user module', user)
                let userInfo = user

                ClientPortalAPIService.getUserSettings().then(userProfile => {
                    console.log('userSettings from user module', userProfile)

                    if (!userProfile.profile || !userProfile.profile?.userSettings) {
                        userInfo.settings = this.userSettings // First time user, we save default settings to his profile
                        
                        // We also save default settings to OKTA
                        this.updateUserSettings(userInfo.settings).then(() => this.hasFetchedUserFromServer = true)
                    } else {
                        // We spread default values and spread overide with existing values, so user always will have eventual new settings
                        const parsedUserSettings = JSON.parse(userProfile.profile.userSettings)
                        userInfo.settings = {
                            ...this.userSettings,
                            ...parsedUserSettings
                        }
                        
                        this.parseUserSettings(parsedUserSettings)
                        this.hasFetchedUserFromServer = true
                    }
                    
                    this.userInfo = userInfo
    
                    resolve(userInfo)
                }).catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
                    
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })

        return userInfo
    }

    async getLocalUserInfo() {
        return await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.hasFetchedUserFromServer) {
                    resolve(this.userInfo)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })
    }

    // This is a useful function that we can use in most cases. Once the app is loaded we keep all the settings in sync both remotly and locally
    // so there is no need to call the server again, as the local should always be in sync
    async getLocalUserSettings() {
        const settings = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.hasFetchedUserFromServer) {
                    resolve(this.userSettings)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return settings
    }

    async fetchUserPermissions() {
        return await new Promise((resolve, reject) => {
            ClientPortalAPIService.getUserPermissionList().then(userPermissions => {
                this.userPermissions = userPermissions
                this.hasFetchedUserPermissionsFromServer = true

                resolve(true)
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })
    }

    async getUserPermissions() {
        const permissions = await new Promise((resolve) => {
            if (this.userPermissions.length > 0) {
                resolve(this.userPermissions)
            } else {
                const observerInterval = window.setInterval(() => {
                    if (this.hasFetchedUserPermissionsFromServer) {
                        resolve(this.userPermissions)
                        window.clearInterval(observerInterval)
                    }
                }, 25)
            }
        })

        return permissions
    }

    parseUserSettings(userSettings) {
        // Get user saved settings, otherwise initialize them, this is needed when new values are added to settings
        this.userSettings.airportCode = userSettings.airportCode !== undefined ? userSettings.airportCode : this.userSettings.airportCode
        this.userSettings.mesaurementUnits = userSettings.mesaurementUnits !== undefined ? userSettings.mesaurementUnits : this.userSettings.mesaurementUnits

        this.userSettings.pinnedElements = userSettings.pinnedElements !== undefined ?
            userSettings.pinnedElements : this.userSettings.pinnedElements
        this.userSettings.airportsWatchlist = userSettings.airportsWatchlist !== undefined ?
            userSettings.airportsWatchlist : this.userSettings.airportsWatchlist
        this.userSettings.followedAirports = userSettings.followedAirports ? userSettings.followedAirports : this.userSettings.followedAirports
        this.userSettings.trackedAircrafts = userSettings.trackedAircrafts !== undefined ? 
            userSettings.trackedAircrafts : this.userSettings.trackedAircrafts
        this.userSettings.routes = userSettings.routes !== undefined ?
            userSettings.routes : this.userSettings.routes
        this.userSettings.latestDismissedCriticalAlertID = userSettings.latestDismissedCriticalAlertID !== undefined ? 
            userSettings.latestDismissedCriticalAlertID : this.userSettings.latestDismissedCriticalAlertID
        this.userSettings.latestDismissedAviationAlertID = userSettings.latestDismissedAviationAlertID !== undefined ? 
            userSettings.latestDismissedAviationAlertID : this.userSettings.latestDismissedAviationAlertID

        if (userSettings.filters !== undefined) {
            let parsedDefaultFilters = JSON.parse(this.userSettings.filters)
            let parsedUserFilters = JSON.parse(userSettings.filters)

            // TODO: This function was introduced in August 2024 during Samdesk removal, it's here to assure that slowly all users will have
            // all their old Samdesk filters removed from their settings, given a considerable amount of time, consider removing this function
            if (parsedDefaultFilters.enableIncidents !== undefined) {
                console.warn('Found old Samdesk filters in user settings, removing them.')
                delete parsedDefaultFilters.enableIncidents
                delete parsedDefaultFilters.incidentFilters
                delete parsedDefaultFilters.severity

                delete parsedUserFilters.enableIncidents
                delete parsedUserFilters.incidentFilters
                delete parsedUserFilters.severity
            }

            // We also need to assure all new filter options are attached if they were introduced later
            for (const key in parsedDefaultFilters) {
                if (parsedUserFilters[key] === undefined) {
                    parsedUserFilters[key] = parsedDefaultFilters[key]
                }
            }

            this.userSettings.filters = JSON.stringify(parsedUserFilters)
        } else {
            this.userSettings.filters = this.userSettings.filters
        }

        if (userSettings.comstat !== undefined) {
            let parsedDefaultComstatSettings = JSON.parse(this.userSettings.comstat)
            let parsedUserComstatSettings = JSON.parse(userSettings.comstat)

            // We also need to assure all new properties are attached if they were introduced later
            for (const key in parsedDefaultComstatSettings) {
                if (parsedUserComstatSettings[key] === undefined) {
                    parsedUserComstatSettings[key] = parsedDefaultComstatSettings[key]
                }
            }

            this.userSettings.comstat = JSON.stringify(parsedUserComstatSettings)
        } else {
            this.userSettings.comstat = this.userSettings.comstat
        }

        this.userSettings.briefBuilder = userSettings.briefBuilder !== undefined ?
            userSettings.briefBuilder : this.userSettings.briefBuilder
    }

    async updateUserSettings(settings) {
        const userSettings = cloneDeep(settings)
        const result = await new Promise((resolve, reject) => {
            ClientPortalAPIService.updateUserSettings({ settings: JSON.stringify(userSettings) }).then(response => {
                if (response.errorSummary !== undefined) {
                    reject(response.errorSummary)
                } else {
                    this.parseUserSettings(JSON.parse(response.profile.UserSettings))
                    resolve(true)
                }
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })

        return result
    }

    async updateUserBriefBuilderSettings(briefBuilderSettings) {
        const result = await new Promise((resolve, reject) => {
            this.userSettings.briefBuilder = JSON.stringify(briefBuilderSettings)

            this.updateUserSettings(this.userSettings).then(response => {
                resolve(response)
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })

        return result
    }

    async updateUserFilters(userFilters) {
        const result = await new Promise((resolve, reject) => {
            this.userSettings.filters = JSON.stringify(userFilters)

            this.updateUserSettings(this.userSettings).then(response => {
                resolve(response)
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })

        return result
    }

    addAirportToUserWatchlist(airportICAO) {
        if (this.userSettings.airportsWatchlist.indexOf(airportICAO) === -1) {
            this.userSettings.airportsWatchlist.unshift(airportICAO)
            this.updateUserSettings(this.userSettings)
        } else {
            window.alert('This airport is already in your watchlist.')
        }
    }

    addAircraftToUserTailsList(tailNumber) {
        if (this.userSettings.trackedAircrafts.indexOf(tailNumber) === -1) {
            this.userSettings.trackedAircrafts.unshift(tailNumber)
            this.updateUserSettings(this.userSettings)
        } else {
            window.alert('You are already tracking this aircraft')
        }
    }

    async addRoute(routePlanString, decodeRouteResult) {
        const result = await new Promise((resolve, reject) => {
            this.userSettings.routes.unshift(JSON.stringify({
                routePlanString: routePlanString,
                decodeRouteResult: decodeRouteResult
            }))

            this.updateUserSettings(this.userSettings).then(response => {
                resolve(response)
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })

        return result
    }

    async removeRoute(routePlanString) {
        const result = await new Promise((resolve, reject) => {
            const existingRouteIndex = this.userSettings.routes.findIndex(routeJSONString => JSON.parse(routeJSONString).routePlanString === routePlanString)

            if (existingRouteIndex > -1) {
                this.userSettings.routes.splice(existingRouteIndex, 1)
                this.updateUserSettings(this.userSettings).then(response => {
                    resolve(response)
                }).catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
            } else {
                reject('Route not found in user settings.')
            }
        })

        return result
    }

    removeAirportFromUserWatchlist(airportICAO) {
        const existingAirportIndex = this.userSettings.airportsWatchlist.findIndex(airportICAOCode => airportICAOCode === airportICAO)
        if (existingAirportIndex > -1) {
            this.userSettings.airportsWatchlist.splice(existingAirportIndex, 1)
            this.updateUserSettings(this.userSettings)
        }
    }
    
    async removeAircraftFromUserTailsList(tailNumber) {
        const result = await new Promise((resolve, reject) => {
            const existingAircraftIndex = this.userSettings.trackedAircrafts.findIndex(flightIdentifier => flightIdentifier === tailNumber)
            if (existingAircraftIndex > -1) {
                this.userSettings.trackedAircrafts.splice(existingAircraftIndex, 1)
                this.updateUserSettings(this.userSettings).then(response => {
                    resolve(response)
                }).catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
            } else {
                reject('Provided tail number ' + tailNumber + ' was not found in user settings.')
            }
        })

        return result
    }
}

const User = new UserModule()
export default User